import { useEffect, useState, MutableRefObject } from 'react';

const useOnScreen = <T extends Element>(
  ref: MutableRefObject<T>,
  minPixel: number = 0,
): { isOnScreen: boolean; isAboveScreen: boolean; isBelowScreen: boolean } => {
  // State and setter for storing whether element is visible
  const [isOnScreen, setOnScreen] = useState<boolean>(false);
  const [isAboveScreen, setAboveScren] = useState<boolean>(false);
  const [isBelowScreen, setBelowScreen] = useState<boolean>(false);

  useEffect(() => {
    let observer: IntersectionObserver;
    try {
      const rootMargin =
        ref.current?.clientHeight > minPixel
          ? `-${minPixel}px`
          : `-${ref.current?.clientHeight || 0}px`;

      observer = new IntersectionObserver(
        ([entry]) => {
          // Update our state when observer callback fires
          setOnScreen(entry.isIntersecting);

          if (entry.boundingClientRect.top > 0) {
            setBelowScreen(true);
            setAboveScren(false);
          } else {
            setAboveScren(true);
            setBelowScreen(false);
          }
        },
        {
          rootMargin,
        },
      );
      if (ref.current) {
        observer.observe(ref.current);
      }
    } catch (e) {
      // ignore errors that come from unsupported browsers for IntersectionObserver
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };

    // Empty array ensures that effect is only run on mount and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isOnScreen, isAboveScreen, isBelowScreen };
};

export default useOnScreen;
